import React, { useMemo } from "react";
import {
  getFacility360Shifts,
  getFacility360Workers,
  getFacility360ShiftStart,
  facility360ToDate,
  normalizeFacility360Value,
} from "../utils/facility360Metrics";

function getShiftDate(shift) {
  return facility360ToDate(
    shift?.completedAt ||
      shift?.arrivedAt ||
      shift?.activatedAt ||
      shift?.assignedAt ||
      shift?.updatedAt
  ) || getFacility360ShiftStart(shift);
}

function formatDateTime(value) {
  const date = facility360ToDate(value);

  if (!date) return "Date unavailable";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function getTone(type) {
  const tones = {
    facility: {
      color: "#0369a1",
      background: "#e0f2fe",
    },
    worker: {
      color: "#7c3aed",
      background: "#ede9fe",
    },
    shift: {
      color: "#166534",
      background: "#dcfce7",
    },
    payroll: {
      color: "#b45309",
      background: "#fef3c7",
    },
    invoice: {
      color: "#1d4ed8",
      background: "#dbeafe",
    },
    warning: {
      color: "#991b1b",
      background: "#fee2e2",
    },
  };

  return tones[type] || tones.facility;
}

export default function Facility360Activity({
  facility,
  shifts = [],
  users = [],
}) {
  const timeline = useMemo(() => {
    const events = [];

    function addEvent({
      id,
      date,
      title,
      description,
      type = "facility",
    }) {
      const parsedDate =
        facility360ToDate(date);

      if (!parsedDate) return;

      events.push({
        id,
        date: parsedDate,
        title,
        description,
        type,
      });
    }

    addEvent({
      id: "facility-created",
      date: facility?.createdAt,
      title: "Facility Created",
      description:
        "Facility profile entered the platform.",
      type: "facility",
    });

    addEvent({
      id: "facility-updated",
      date: facility?.updatedAt,
      title: "Facility Profile Updated",
      description:
        "Facility configuration or profile information was updated.",
      type: "facility",
    });

    const facilityWorkers =
      getFacility360Workers(
        facility,
        users
      );

    facilityWorkers.forEach(
      (worker, index) => {
        const workerId =
          worker.uid || worker.id || index;

        const workerName =
          worker.displayName ||
          worker.fullName ||
          [
            worker.firstName,
            worker.lastName,
          ]
            .filter(Boolean)
            .join(" ") ||
          worker.email ||
          "Worker";

        addEvent({
          id: `worker-${workerId}`,
          date:
            worker.facilityAssignedAt ||
            worker.readyAt ||
            worker.workerApprovedAt ||
            worker.updatedAt,
          title: "Worker Connected",
          description: `${workerName} is connected to this facility workforce.`,
          type: "worker",
        });
      }
    );

    const facilityShifts =
      getFacility360Shifts(
        facility,
        shifts
      );

    facilityShifts.forEach(
      (shift, index) => {
        const shiftId =
          shift.id || shift.shiftId || index;

        const status =
          normalizeFacility360Value(
            shift.status || "updated"
          );

        const role = String(
          shift.role ||
            shift.requiredPosition ||
            shift.position ||
            "Shift"
        ).toUpperCase();

        const workerName =
          shift.assignedWorkerName ||
          shift.workerName ||
          shift.assignedWorkerDisplayName ||
          "Unassigned";

        addEvent({
          id: `shift-${shiftId}`,
          date: getShiftDate(shift),
          title: `Shift ${titleCase(status)}`,
          description: `${role} · ${workerName}`,
          type: [
            "cancelled",
            "canceled",
            "no_show",
            "no-show",
            "noshow",
          ].includes(status)
            ? "warning"
            : "shift",
        });

        const payrollStatus =
          normalizeFacility360Value(
            shift.payrollStatus
          );

        if (payrollStatus) {
          addEvent({
            id: `payroll-${shiftId}`,
            date:
              shift.payrollUpdatedAt ||
              shift.paidAt ||
              shift.completedAt ||
              shift.updatedAt,
            title: `Payroll ${titleCase(
              payrollStatus
            )}`,
            description: `Payroll status updated for the ${role} shift.`,
            type: "payroll",
          });
        }

        const invoiceStatus =
          normalizeFacility360Value(
            shift.invoiceStatus
          );

        if (invoiceStatus) {
          addEvent({
            id: `invoice-${shiftId}`,
            date:
              shift.invoiceUpdatedAt ||
              shift.invoicedAt ||
              shift.completedAt ||
              shift.updatedAt,
            title: `Invoice ${titleCase(
              invoiceStatus
            )}`,
            description: `Invoice status updated for the ${role} shift.`,
            type: "invoice",
          });
        }
      }
    );

    return events.sort(
      (a, b) =>
        b.date.getTime() - a.date.getTime()
    );
  }, [facility, shifts, users]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Facility Activity
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Derived facility, workforce, shift, payroll,
          and invoice activity.
        </div>
      </div>

      <div
        style={{
          padding: 13,
          borderRadius: 14,
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            color: "#64748b",
            fontSize: 10,
            fontWeight: 900,
            textTransform: "uppercase",
          }}
        >
         Derived Events
        </div>

        <div
          style={{
            marginTop: 5,
            color: "#0f172a",
            fontSize: 22,
            fontWeight: 950,
          }}
        >
          {timeline.length}
        </div>
      </div>

      {timeline.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            fontWeight: 750,
          }}
        >
          No dated facility activity is available.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          {timeline.map((event) => {
            const tone = getTone(event.type);

            return (
              <div
                key={event.id}
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "36px 1fr",
                  gap: 10,
                  padding: 13,
                  borderRadius: 14,
                  background: "#ffffff",
                  border:
                    "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 999,
                    color: tone.color,
                    background:
                      tone.background,
                    fontSize: 14,
                    fontWeight: 950,
                  }}
                >
                  •
                </div>

                <div>
                  <div
                    style={{
                      color: "#0f172a",
                      fontSize: 13,
                      fontWeight: 950,
                    }}
                  >
                    {event.title}
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      color: "#64748b",
                      fontSize: 11,
                      fontWeight: 750,
                    }}
                  >
                    {formatDateTime(event.date)}
                  </div>

                  <div
                    style={{
                      marginTop: 6,
                      color: "#475569",
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.45,
                    }}
                  >
                    {event.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}